'use client';

import { logger } from '@adornis/base/logging.js';
import { Menu } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getStudentByUserIdQuery } from '../../db/api/get-student.js';
import { getTeacherByUserIdQuery } from '../../db/api/get-teacher.js';
import type { ViewState } from '../app/types.js';
import { Chat } from '../components/chat/chat.js';
import { ClassInsightsPage } from '../components/pages/class-insights-page.js';
import { ClassroomPage } from '../components/pages/classroom-page.js';
import { DummyView } from '../components/pages/dummy.js';
import { FeedbackPage } from '../components/pages/feedback-page.js';
import { JoinClassPage } from '../components/pages/join-class-page.js';
import { ProfilePage } from '../components/pages/profile-page.js';
import { SettingsPage } from '../components/pages/settings-page.js';
import { ExamNotificationToaster } from '../components/pop-up/exam-notification-toaster.js';
import { Sidebar } from '../components/sidebar/sidebar.js';
import { logout } from '@adornis/users/client/loginFunctions.js';
import { ResultLinkAccountDialog } from '../components/result-link-account-dialog.js';
import { triggerExamUpdate } from '../lib/events.js';
import {
  claimGuestConversationAfterLogin,
  clearGuestConversationId,
  clearGuestMessageCount,
} from '../lib/guest-user-id.js';
import { consumePendingJoinCode } from '../lib/pending-join-code.js';
import {
  consumePendingResultLink,
  discardPendingResultLink,
  isResultLinkFor,
  peekPendingResultLink,
  stashResultLinkFromUrl,
  type PendingResultLink,
} from '../lib/pending-result-link.js';
import { useCurrentUser } from '../lib/useCurrentUser.js';

type HomeProps = {
  onNavigate?: (view: ViewState, title?: string) => void;
  onOpenLogin?: () => void;
  onOpenSignup?: () => void;
  onGuestMessageSent?: (conversationId: string) => void;
};

export default function Home({ onNavigate, onOpenLogin, onOpenSignup, onGuestMessageSent }: HomeProps) {
  const { user } = useCurrentUser();
  const { i18n } = useTranslation();
  const [currentView, setCurrentView] = useState<ViewState>('chat');
  const [sidebarTitle, setSidebarTitle] = useState('');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [conversationId, setConversationId] = useState<string>('new');
  const [chatNavigationVersion, setChatNavigationVersion] = useState(0);
  const [pendingJoinCode, setPendingJoinCode] = useState<string | null>(null);
  const [pendingResultLink, setPendingResultLink] = useState<PendingResultLink | null>(null);
  const [showResultLinkAccountDialog, setShowResultLinkAccountDialog] = useState(false);
  const prevUserIdRef = useRef<string | null | undefined>(undefined);
  const isGuest = !user;

  const handleNavigate = (view: ViewState, title?: string) => {
    setCurrentView(view);
    if (view === 'chat') {
      setChatNavigationVersion(prev => prev + 1);
      if (title) {
        setConversationId(title);
      }
    }
    setSidebarTitle(title ?? '');
  };

  // Wenn sich der angemeldete Benutzer ändert (Login / Logout), laden wir die Chat-Komponenten neu.
  // `currentUser()` kann während eines Logins mehrfach emittieren – wir reagieren daher nur auf einen
  // echten Identitätswechsel (per Ref), sonst würde eine spätere Emission die Navigation zurücksetzen.
  // Bei Login übernehmen wir die Gast-Konversation dieses Tabs ins Konto und navigieren dorthin statt zum
  // Dashboard ('new'). Das gilt für jede Login-Art – auch nach dem vollen Seiten-Redirect des Google-Logins.
  useEffect(() => {
    const currentUserId = (user as any)?._id ?? null;
    if (prevUserIdRef.current === currentUserId) return;
    prevUserIdRef.current = currentUserId;

    setConversationId('new');
    setChatNavigationVersion(prev => prev + 1);

    if (currentUserId) {
      void claimGuestConversationAfterLogin().then(claimedId => {
        // Inzwischen ein anderer Nutzer (z. B. Logout)? Dann nicht mehr dorthin navigieren.
        if (!claimedId || prevUserIdRef.current !== currentUserId) return;
        setConversationId(claimedId);
        setChatNavigationVersion(prev => prev + 1);
        // The sidebar loaded its list on the user change, before the claim moved the exam to the account.
        triggerExamUpdate();
      });
    }

    // A join code stashed by the `/join?code=` landing page (see client/app/join/page.tsx) survives
    // the login/signup round-trip in localStorage; once we have a real user, consume it and jump
    // straight into the join flow instead of landing on the dashboard.
    if (currentUserId) {
      const pendingCode = consumePendingJoinCode();
      if (pendingCode) {
        setPendingJoinCode(pendingCode);
        handleNavigate('joinClass');
      }
    }

    // Same round-trip for the link in a results mail: stashed on arrival, opened once the account it was sent to is
    // signed in. Students find their results under their classes, which is the join-class view. Another account
    // signed in (a teacher, someone else on a shared computer): ask, and keep the link for the right account instead
    // of opening an empty class list in this one. Signed out: nothing to do until someone signs in — the login is NOT
    // opened from here, because on a page load the user is briefly null while the session is still being restored,
    // and opening it then would cover the session that is about to arrive.
    stashResultLinkFromUrl();
    const resultLink = peekPendingResultLink();
    setShowResultLinkAccountDialog(false);
    if (resultLink && currentUserId) {
      if (isResultLinkFor(resultLink, currentUserId)) {
        consumePendingResultLink();
        setPendingResultLink(resultLink);
        handleNavigate('joinClass');
      } else {
        setShowResultLinkAccountDialog(true);
      }
    }
  }, [user]);

  const handleResultLinkSwitchAccount = async () => {
    setShowResultLinkAccountDialog(false);
    try {
      try {
        clearGuestConversationId();
      } catch (e) {}
      try {
        clearGuestMessageCount();
      } catch (e) {}
      // The link stays stashed; the effect above opens it once the right account has signed in. Here, unlike on a
      // page load, the user is known to be signed out, so the login can open right away.
      await logout();
      onOpenLogin?.();
    } catch (err) {
      logger.error(err, 'Logout for result link failed');
    }
  };

  // Gäste haben keinen Zugriff auf die Profilseite (z. B. bei Logout während des Betrachtens).
  useEffect(() => {
    if (
      isGuest &&
      (currentView === 'profile' ||
        currentView === 'classInsights' ||
        currentView === 'classroom' ||
        currentView === 'joinClass')
    ) {
      handleNavigate('chat');
    }
  }, [isGuest, currentView]);

  useEffect(() => {
    const userId = (user as any)?._id;
    if (!userId) return;

    let isActive = true;

    void Promise.all([
      getTeacherByUserIdQuery(userId)({ language: 1 }),
      getStudentByUserIdQuery(userId)({ language: 1 }),
    ])
      .then(([teachers, students]) => {
        if (!isActive) return;
        const preferredLanguage = String(teachers?.[0]?.language || students?.[0]?.language || '').trim();
        if (preferredLanguage && preferredLanguage !== i18n.language) {
          void i18n.changeLanguage(preferredLanguage).catch(err => {
            logger.error(err, 'Failed to apply user language preference');
          });
        }
      })
      .catch(err => {
        logger.error(err, 'Failed to load user language preference');
      });

    return () => {
      isActive = false;
    };
  }, [user, i18n]);

  return (
    <main className="flex h-screen overflow-hidden bg-background">
      <Sidebar
        onNavigate={handleNavigate}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onCollapsedChange={setIsSidebarCollapsed}
        onOpenLogin={onOpenLogin}
        onOpenSignup={onOpenSignup}
        activeConversationId={conversationId}
      />

      {!isMobileSidebarOpen && (
        <button
          onClick={() => setIsMobileSidebarOpen(true)}
          className="fixed top-4 left-4 z-30 md:hidden bg-brand-strong text-white p-3 rounded-lg shadow-lg hover:bg-brand-700 transition-colors"
        >
          <Menu className="h-6 w-6" />
        </button>
      )}

      {/* z-0 pins its own stacking context so nothing inside — including body-portaled floating
          panels like the exam editor's popovers — can ever paint over the sidebar/app chrome,
          no matter what z-index that content uses internally. */}
      <section className="flex-1 flex flex-col h-full relative z-0 overflow-hidden">
        {currentView === 'chat' ? (
          <Chat
            conversationId={conversationId}
            navigationVersion={chatNavigationVersion}
            onMessageSent={id => {
              setIsSidebarCollapsed(true);
              onGuestMessageSent?.(id);
            }}
            onOpenLogin={onOpenLogin}
            onOpenSignup={onOpenSignup}
            onConversationIdChange={setConversationId}
          />
        ) : currentView === 'profile' && !isGuest ? (
          <ProfilePage onBack={() => handleNavigate('chat')} />
        ) : currentView === 'classInsights' && !isGuest ? (
          <ClassInsightsPage
            className={sidebarTitle}
            onBack={() => handleNavigate('profile')}
            onOpenExam={conversationId => handleNavigate('chat', conversationId)}
          />
        ) : currentView === 'classroom' && !isGuest ? (
          <ClassroomPage
            onBack={() => handleNavigate('chat')}
            onOpenExam={conversationId => handleNavigate('chat', conversationId)}
          />
        ) : currentView === 'joinClass' && !isGuest ? (
          <JoinClassPage
            onBack={() => handleNavigate('chat')}
            initialCode={pendingJoinCode}
            onInitialCodeConsumed={() => setPendingJoinCode(null)}
            initialResultLink={pendingResultLink}
            onInitialResultLinkConsumed={() => setPendingResultLink(null)}
          />
        ) : currentView === 'feedback' ? (
          <FeedbackPage onBack={() => handleNavigate('chat')} />
        ) : currentView === 'settings' ? (
          <SettingsPage onBack={() => handleNavigate('chat')} />
        ) : (
          <DummyView title={sidebarTitle} onBack={() => handleNavigate('chat')} />
        )}
      </section>

      <ResultLinkAccountDialog
        open={showResultLinkAccountDialog}
        onSwitchAccount={handleResultLinkSwitchAccount}
        onDismiss={() => {
          discardPendingResultLink();
          setShowResultLinkAccountDialog(false);
        }}
      />

      <ExamNotificationToaster
        onOpenExam={conversationId => handleNavigate('chat', conversationId)}
        activeConversationId={conversationId}
      />
    </main>
  );
}
